/**
 * @fileoverview Resize setup - レイアウト管理とビューポート管理
 */

import { MinimalErrorTracker } from '../shared/log.js';

const CSS_PROP_BOARD_SCALE = '--board-scale';
const CSS_PROP_PIECE_SIZE = '--piece-size';
const CSS_PROP_BOARD_WIDTH = '--board-width';
const CSS_PROP_BOARD_HEIGHT = '--board-height';
const CSS_PROP_BOARD_INNER_PADDING = '--board-inner-padding';
const DOC_STYLE = document.documentElement.style;

// シンプルなデバウンス - 入力制御ユーティリティ

/**
 * 入力制御用のデバウンス関数を作成
 * @param {Function} func - デバウンスする関数
 * @param {number} wait - 遅延するミリ秒数
 * @returns {Function} デバウンスされた関数
 */
export function debounce(func, wait) {
  let timeout;

  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

// ビューポート管理関数をインポート
import { updateViewportHeight, scrollLock } from './viewport.js';

// レイアウト設定定数
const LAYOUT_CONFIG = {
  // デザインキャンバス寸法
  DESIGN_WIDTH: 720,
  DESIGN_HEIGHT: 1280,

  // GapY分散のセグメント重み
  WEIGHTS: {
    topToY1: 4,
    y1ToY2: 1,
    y2ToY3: 1,
    y3ToY4: 1,
    y4ToBottom: 8,
  },

  // デザイン空間の絶対中心位置
  ANCHORS: {
    matchY: 80, // Y1
    gameHeaderY: 250, // Y2
    boardY: 700, // Y3
    // Y4は導出値
    startGapFromBoardBottom: 110,
  },

  // フォールバックボード設定
  FALLBACK: {
    rows: 9,
    cols: 9,
    pieceSize: 70,
    boardHeightDesign: 654,
  },

  // パフォーマンス設定
  DEBOUNCE_DELAY: 16,
};

// エラー回復用のフォールバックボードサイズ設定
function applyFallbackBoardSize() {
  try {
    // プレイ可能なゲームボードを保証する安全なデフォルト値
    const { rows, cols, pieceSize } = LAYOUT_CONFIG.FALLBACK;
    const fallbackInnerPadding = Math.max(12, Math.round(pieceSize * 0.2));

    const fallbackBoardWidth = `${cols * pieceSize + fallbackInnerPadding * 2}px`;
    const fallbackBoardHeight = `${rows * pieceSize + fallbackInnerPadding * 2}px`;

    DOC_STYLE.setProperty(CSS_PROP_PIECE_SIZE, `${pieceSize}px`);
    DOC_STYLE.setProperty(CSS_PROP_BOARD_WIDTH, fallbackBoardWidth);
    DOC_STYLE.setProperty(CSS_PROP_BOARD_HEIGHT, fallbackBoardHeight);
    DOC_STYLE.setProperty(CSS_PROP_BOARD_INNER_PADDING, `${fallbackInnerPadding}px`);

    MinimalErrorTracker.info(
      'SYSTEM',
      `Applied fallback board size: ${rows}x${cols}, piece: ${pieceSize}px`,
    );
  } catch (fallbackError) {
    MinimalErrorTracker.critical('SYSTEM', 'Even fallback board sizing failed', fallbackError);
    // 最後の手段: CSSデフォルトに任せる
  }
}

function computeBoardScale(config) {
  const { DESIGN_WIDTH, DESIGN_HEIGHT } = config;
  const width = window.innerWidth;
  const height = window.innerHeight;
  const scaleToFitX = width / DESIGN_WIDTH;
  const scaleToFitY = height / DESIGN_HEIGHT;
  return Math.min(scaleToFitX, scaleToFitY);
}

function syncBoardDimensions() {
  try {
    const { ROWS, COLS, PIECE_SIZE } = window.__GAME_CONSTANTS__ || {};
    if (typeof ROWS === 'number' && typeof COLS === 'number' && typeof PIECE_SIZE === 'number') {
      const innerPadding = Math.max(12, Math.round(PIECE_SIZE * 0.2));
      const boardWidth = `${COLS * PIECE_SIZE + innerPadding * 2}px`;
      const boardHeight = `${ROWS * PIECE_SIZE + innerPadding * 2}px`;
      DOC_STYLE.setProperty(CSS_PROP_PIECE_SIZE, `${PIECE_SIZE}px`);
      DOC_STYLE.setProperty(CSS_PROP_BOARD_WIDTH, boardWidth);
      DOC_STYLE.setProperty(CSS_PROP_BOARD_HEIGHT, boardHeight);
      DOC_STYLE.setProperty(CSS_PROP_BOARD_INNER_PADDING, `${innerPadding}px`);
      return;
    }
    applyFallbackBoardSize();
  } catch (error) {
    MinimalErrorTracker.warn('SYSTEM', 'Failed to sync board size with game constants', error);
    applyFallbackBoardSize();
  }
}

function resolveBoardHeightDesign(config) {
  const { FALLBACK } = config;
  try {
    const css = getComputedStyle(document.documentElement);
    const px = (name) => parseFloat(css.getPropertyValue(name)) || 0;
    const boardHeightDesign = px(CSS_PROP_BOARD_HEIGHT);
    if (boardHeightDesign <= 0) {
      return FALLBACK.boardHeightDesign;
    }
    return boardHeightDesign;
  } catch (error) {
    MinimalErrorTracker.warn(
      'SYSTEM',
      'Failed to fetch CSS variables for layout calculation',
      error,
    );
    return FALLBACK.boardHeightDesign;
  }
}

function calculateLayoutPositions(config, scale, boardHeightDesign) {
  const { ANCHORS, DESIGN_HEIGHT, WEIGHTS } = config;
  const windowHeight = window.innerHeight;
  const y1Design = ANCHORS.matchY;
  const y2Design = ANCHORS.gameHeaderY;
  const y3Design = ANCHORS.boardY;
  const y4Design = y3Design + boardHeightDesign / 2 + ANCHORS.startGapFromBoardBottom;
  const y5Design = DESIGN_HEIGHT;

  const y1Scaled = y1Design * scale;
  const y2Scaled = y2Design * scale;
  const y3Scaled = y3Design * scale;
  const y4Scaled = y4Design * scale;
  const y5Scaled = y5Design * scale;

  const gapY = windowHeight - y5Scaled;
  const { topToY1, y1ToY2, y2ToY3, y3ToY4, y4ToBottom } = WEIGHTS;
  const totalWeight = topToY1 + y1ToY2 + y2ToY3 + y3ToY4 + y4ToBottom;
  const distribute = (weight) => (gapY > 0 ? (gapY * weight) / totalWeight : 0);

  const s0 = distribute(topToY1);
  const s1 = distribute(y1ToY2);
  const s2 = distribute(y2ToY3);
  const s3 = distribute(y3ToY4);

  const c0 = s0;
  const c1 = s0 + s1;
  const c2 = s0 + s1 + s2;
  const c3 = s0 + s1 + s2 + s3;

  return {
    y1: y1Scaled + c0,
    y2: y2Scaled + c1,
    y3: y3Scaled + c2,
    y4: y4Scaled + c3,
  };
}

function applyLayoutPositions(positions) {
  const setPx = (name, valuePx) => {
    try {
      DOC_STYLE.setProperty(name, `${Math.round(valuePx)}px`);
    } catch (error) {
      MinimalErrorTracker.warn('SYSTEM', `Failed to set CSS variable ${name}`, error);
    }
  };

  try {
    setPx('--abs-y1', positions.y1);
    setPx('--abs-y2', positions.y2);
    setPx('--abs-y3', positions.y3);
    setPx('--abs-y4', positions.y4);
  } catch (error) {
    MinimalErrorTracker.warn('SYSTEM', 'Failed to apply layout positions', error);
  }
}

function resizeGame() {
  updateViewportHeight();

  const scale = computeBoardScale(LAYOUT_CONFIG);
  DOC_STYLE.setProperty(CSS_PROP_BOARD_SCALE, String(scale));

  syncBoardDimensions();

  const boardHeightDesign = resolveBoardHeightDesign(LAYOUT_CONFIG);
  const positions = calculateLayoutPositions(LAYOUT_CONFIG, scale, boardHeightDesign);
  applyLayoutPositions(positions);
}

// パッシブ最適化とデバウンス付きイベントリスナー
const debouncedResize = debounce(resizeGame, LAYOUT_CONFIG.DEBOUNCE_DELAY);
window.addEventListener('resize', debouncedResize, { passive: true });
window.addEventListener('orientationchange', debouncedResize, { passive: true });

// DOM準備完了時に一度初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => requestAnimationFrame(resizeGame), {
    once: true,
  });
} else {
  requestAnimationFrame(resizeGame);
}

// 初回スケール適用後のフェードイン処理
(function () {
  let bootCleared = false;
  const clearBoot = async () => {
    if (bootCleared) return;
    bootCleared = true;

    // 制限システム表示初期化をリサイズ完了後に実行
    try {
      const { resetLimitDisplay } = await import('../presentation/controllers/limit-controller.js');
      const gameState = (await import('../core/game-state.js')).default;

      // Title状態では常に0を表示（reset()は不要、デフォルトで0）
      resetLimitDisplay(gameState);
    } catch (error) {
      // リサイズ後の制限システム初期化失敗は非致命的
      MinimalErrorTracker.warn('SYSTEM', 'Limit system initialization after resize failed', error);
    }

    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        document.documentElement.classList.remove('is-booting');
      });
    } else {
      setTimeout(() => {
        document.documentElement.classList.remove('is-booting');
      }, 16);
    }
  };

  // DOMContentLoadedかloadのどちらか早いイベント後のresize実行で解除
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        resizeGame();
        clearBoot();
      },
      { once: true },
    );
  } else {
    // 既に読み込み済みなら今すぐ適用して解除
    resizeGame();
    clearBoot();
  }

  window.addEventListener(
    'load',
    () => {
      clearBoot();
    },
    { once: true },
  );
})();

// 他のモジュールで使用するための関数をエクスポート
export { resizeGame, scrollLock };
